import { isNative, isWeb } from "@stepapp/core";
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type KeyboardTypeOptions,
  type NativeSyntheticEvent,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  type TextInputFocusEventData,
} from "react-native";
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated";

import { getMaskValue, MaskType } from "../../helpers";
import { palette, spacing } from "../../styles";
import Block from "../Block";
import Typography from "../Typography";
import { typographyVariants } from "../Typography/variants";
import { type InputProps, MaxLength } from "./types";

const Input = forwardRef<TextInput, InputProps>(
  (
    {
      asButton = false,
      containerStyle,
      editable = true,
      error,
      inputStyle,
      keyboardType,
      label,
      mask: propMask,
      maxCharacters,
      maxLength,
      multiline,
      onBlur,
      onChangeText,
      onFocus,
      placeholder,
      placeholderTextColor,
      prefixComponent,
      required = false,
      showAnimatedLabel = true,
      showErrorPlaceholder = true,
      style,
      suffixComponent,
      translateLabelX = -28,
      value,
      ...inputProps
    },
    ref,
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO: refactor
  ) => {
    const inputRef = useRef<TextInput>(null);
    const textInputRef = ref || inputRef;
    const [focused, setFocused] = useState(false);
    const animatedLabelFocused = useSharedValue(value?.length > 0 ? 1 : 0);
    const animatedLabel = isNative && placeholder && !multiline && showAnimatedLabel;
    const inputEnabled = editable && !asButton;
    const countryCode = "countryCode" in inputProps ? inputProps.countryCode : undefined;
    const mask = propMask as MaskType;

    const inputValue = useMemo(() => {
      return mask && value ? getMaskValue({ countryCode, mask, rawValue: value }) : value;
    }, [mask, value, countryCode]);

    const handleTextChange = (text: string): void => {
      const rawValue = mask ? text.replace(/ /gi, "") : text;
      onChangeText?.(rawValue);
    };

    const handleFocus = useCallback(
      (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
        animatedLabelFocused.value = 1;
        setFocused(true);
        typeof onFocus === "function" && onFocus(event);
      },
      [onFocus, animatedLabelFocused],
    );

    const handleBlur = useCallback(
      (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
        setFocused(false);
        animatedLabelFocused.value = inputValue?.length > 0 ? 1 : 0;
        typeof onBlur === "function" && onBlur(event);
      },
      [onBlur, animatedLabelFocused, inputValue],
    );

    const maxInputLength = useMemo(
      () => (mask ? MaxLength[mask as "BankAccount"] : maxLength),
      [mask, maxLength],
    );

    const animatedLabelStyles = useAnimatedStyle(() => {
      const translateY = interpolate(animatedLabelFocused.value, [0, 1], [18, -10]);
      const translateX = interpolate(animatedLabelFocused.value, [0, 1], [14, translateLabelX]);
      const springConfig = { damping: 100, stiffness: 100 };
      const scale = animatedLabelFocused.value > 0 ? 0.8 : 1;

      return {
        transform: [
          { translateY: withSpring(translateY, springConfig) },
          { scale: withSpring(scale, springConfig) },
          { translateX: withSpring(translateX, springConfig) },
        ],
        backgroundColor: "transparent",
        zIndex: 5,
        alignItems: "flex-start",
      };
    }, [animatedLabelFocused]);

    const handleInputFocus = useCallback(() => {
      if (textInputRef && "current" in textInputRef) {
        textInputRef?.current?.focus();
      }
    }, [textInputRef]);

    useEffect(() => {
      animatedLabelFocused.value = inputValue?.length > 0 ? 1 : 0;
    }, [inputValue?.length, animatedLabelFocused]);

    return (
      <Block
        pointerEvents={inputEnabled ? "auto" : "none"}
        style={[styles.container, containerStyle]}
      >
        {!!label && (
          <Block marginBottom={1} marginTop={0.5}>
            <Typography type="secondary">
              {label}
              {required && <Typography type="secondary">*</Typography>}
            </Typography>
          </Block>
        )}

        {animatedLabel && (
          <Pressable onPress={handleInputFocus} style={styles.animatedLabel}>
            <Animated.View style={animatedLabelStyles}>
              <Block
                style={[
                  styles.animatedLabelText,
                  {
                    backgroundColor:
                      !editable && inputValue?.length === 0
                        ? palette.common.transparent
                        : palette.common.white,
                  },
                ]}
              >
                <Typography type="secondary" variant="paragraph">
                  {placeholder}
                  {required && <Typography type="lightError">*</Typography>}
                </Typography>
              </Block>
            </Animated.View>
          </Pressable>
        )}

        {!!prefixComponent && <Block style={styles.prefix}>{prefixComponent()}</Block>}

        <Block justifyCenter style={[style, styles.textContainer]}>
          <TextInput
            {...inputProps}
            editable={inputEnabled}
            keyboardType={getKeyboardType({ keyboardType, mask })}
            maxLength={maxInputLength}
            multiline={multiline}
            onBlur={handleBlur}
            onChangeText={handleTextChange}
            onFocus={handleFocus}
            ref={textInputRef}
            // KAV does not work for multiline inputs https://github.com/facebook/react-native/issues/16826
            scrollEnabled={false}
            style={[
              styles.input,
              multiline && styles.multilineInput,
              focused && styles.focused,
              !!error && styles.errorInput,
              !editable && !asButton && styles.inactiveInput,
              inputStyle,
            ]}
            value={inputValue}
          />

          {(isWeb || multiline) && inputValue?.length === 0 && (
            <Typography
              onPress={handleInputFocus}
              style={[
                inputStyle,
                styles.placeholder,
                { color: placeholderTextColor || palette.font.secondary },
              ]}
              variant="paragraph"
            >
              {placeholder}
            </Typography>
          )}

          {showErrorPlaceholder && (
            <Block marginLeft={2} style={styles.error}>
              {error && (
                <Typography id="form-has-error" type="error" variant="lightLabel">
                  {typeof error === "string" && error}
                </Typography>
              )}
            </Block>
          )}

          {!!suffixComponent && <Block style={styles.suffix}>{suffixComponent()}</Block>}
        </Block>

        <Block style={styles.bottomRow}>
          {multiline && maxCharacters && (
            <Typography
              style={styles.counter}
              type="secondary"
              variant="label"
            >{`${value?.length}/${maxCharacters}`}</Typography>
          )}
        </Block>
      </Block>
    );
  },
);

export default React.memo(Input);

const styles = StyleSheet.create({
  animatedLabel: {
    flex: 1,
    left: -4,
    position: "absolute",
    right: 0,
    zIndex: 10,
  },
  animatedLabelText: {
    borderRadius: 4,
    marginLeft: 2,
    overflow: "hidden",
    paddingHorizontal: 4,
  },
  bottomRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  container: {
    position: "relative",
  },
  counter: {
    alignSelf: "flex-end",
    flex: 1,
    textAlign: "right",
  },
  error: {
    height: spacing.unit * 2.5,
  },
  errorInput: {
    borderColor: palette.font.error,
  },
  focused: {
    borderWidth: spacing.border,
    elevation: 4,
    ...Platform.select({
      default: {
        shadowColor: palette.common.disabled,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.4,
        shadowRadius: 4,
      },
    }),
  },
  inactiveInput: {
    backgroundColor: palette.common.grayBackground,
    borderColor: palette.secondary.grey,
  },
  input: {
    ...typographyVariants.input,
    alignContent: "center",
    backgroundColor: palette.common.white,
    borderColor: palette.common.mediumGray,
    borderRadius: spacing.radius,
    borderWidth: spacing.border,
    color: palette.font.primary,
    paddingHorizontal: spacing.unit * 2,
    paddingVertical: spacing.unit * 2,
  },
  multilineInput: {
    borderColor: palette.font.placeholder,
    borderRadius: spacing.radius,
    borderWidth: spacing.border,
    minHeight: spacing.unit * 16,
    paddingTop: spacing.unit * 2,
    padding: spacing.unit,
    verticalAlign: "top",
    alignContent: "flex-start",
    // temporary set default font for multiline input as it does not expand correctly on custom font
    fontFamily: "",
    lineHeight: 18,
    fontSize: 16,
  },
  placeholder: {
    left: spacing.unit * 2,
    position: "absolute",
    right: spacing.unit * 2,
    top: spacing.unit * 2,
  },
  prefix: {
    left: spacing.unit * 2.5,
    position: "absolute",
    top: spacing.unit * 2.25,
    zIndex: 10,
  },
  suffix: {
    position: "absolute",
    right: spacing.unit * 2.5,
    top: spacing.unit * 2.5,
  },
  textContainer: {
    zIndex: 2,
  },
});

function getKeyboardType({
  keyboardType,
  mask,
}: {
  keyboardType?: KeyboardTypeOptions;
  mask?: MaskType;
}) {
  if (keyboardType) return keyboardType;

  switch (mask) {
    case "BankAccount":
    case "PhoneNumber":
    case "PostalCode":
      return "number-pad";
    case "BirthDate":
      return "numbers-and-punctuation";
    default:
      return "default";
  }
}
